import { useEffect, useRef, useState, useCallback } from 'react';
import { Canvas, FabricImage, Rect, Circle, Line, IText, PencilBrush, FabricObject } from 'fabric';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MousePointer,
  Square,
  Circle as CircleIcon,
  MoveUpRight,
  Type,
  Pencil,
  Undo,
  Trash2,
  Check,
} from 'lucide-react';
import { cn } from '../../lib/utils';

type Tool = 'select' | 'arrow' | 'rectangle' | 'circle' | 'text' | 'draw';

const COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#ffffff', // white
  '#000000', // black
];

interface ImageAnnotatorProps {
  imageSrc: string;
  onSave: (blob: Blob) => void | Promise<void>;
  onCancel: () => void;
}

export function ImageAnnotator({ imageSrc, onSave, onCancel }: ImageAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [activeColor, setActiveColor] = useState('#ef4444');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const canvas = new Canvas(canvasRef.current, {
      selection: true,
      backgroundColor: '#1a1a1a',
    });
    fabricRef.current = canvas;

    // Load image with cache-busting to ensure CORS headers are present
    // Browser may have cached the image without CORS headers from normal <img> display
    const cacheBuster = `?t=${Date.now()}`;
    const imageUrl = imageSrc.includes('?') ? `${imageSrc}&_cb=${Date.now()}` : `${imageSrc}${cacheBuster}`;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const maxWidth = container.clientWidth - 32;
      const maxHeight = container.clientHeight - 32;

      let scale = 1;
      if (img.width > maxWidth || img.height > maxHeight) {
        scale = Math.min(maxWidth / img.width, maxHeight / img.height);
      }

      const canvasWidth = img.width * scale;
      const canvasHeight = img.height * scale;

      canvas.setDimensions({
        width: canvasWidth,
        height: canvasHeight,
      });

      // Use the already-loaded image element to avoid CORS issues with double-loading
      const fabricImg = new FabricImage(img);
      fabricImg.scaleToWidth(canvasWidth);
      fabricImg.set({
        left: 0,
        top: 0,
        selectable: false,
        evented: false,
      });
      canvas.add(fabricImg);
      canvas.sendObjectToBack(fabricImg);
      canvas.renderAll();

      // Save initial state
      saveHistory();
    };
    img.onerror = (e) => {
      console.error('Failed to load image for annotation:', e);
    };
    img.src = imageUrl;

    return () => {
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [imageSrc]);

  // Save state to history
  const saveHistory = useCallback(() => {
    if (!fabricRef.current) return;
    const json = JSON.stringify(fabricRef.current.toJSON());
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(json);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (!fabricRef.current || historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    fabricRef.current.loadFromJSON(history[newIndex]).then(() => {
      fabricRef.current?.renderAll();
      setHistoryIndex(newIndex);
    });
  }, [history, historyIndex]);

  // Clear all annotations
  const clearAll = useCallback(() => {
    if (!fabricRef.current) return;
    const objects = fabricRef.current.getObjects();
    // Keep the background image
    const bgImage = objects[0];
    fabricRef.current.clear();
    if (bgImage) {
      fabricRef.current.add(bgImage);
    }
    fabricRef.current.renderAll();
    saveHistory();
  }, [saveHistory]);

  // Handle tool change
  useEffect(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    if (activeTool === 'draw') {
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush = new PencilBrush(canvas);
      canvas.freeDrawingBrush.color = activeColor;
      canvas.freeDrawingBrush.width = 3;
    } else {
      canvas.isDrawingMode = false;
    }

    if (activeTool === 'select') {
      canvas.selection = true;
      canvas.forEachObject((obj) => {
        if (obj.type !== 'image') {
          obj.selectable = true;
          obj.evented = true;
        }
      });
    } else {
      canvas.selection = false;
      canvas.forEachObject((obj) => {
        obj.selectable = false;
        obj.evented = false;
      });
    }
  }, [activeTool, activeColor]);

  // Handle mouse events for shape drawing
  useEffect(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    let tempObject: FabricObject | null = null;

    const handleMouseDown = (e: any) => {
      if (activeTool === 'select' || activeTool === 'draw') return;
      if (!e.pointer) return;

      setIsDrawing(true);
      setDrawStart({ x: e.pointer.x, y: e.pointer.y });

      if (activeTool === 'text') {
        const text = new IText('Text', {
          left: e.pointer.x,
          top: e.pointer.y,
          fontSize: 20,
          fill: activeColor,
          fontFamily: 'sans-serif',
        });
        canvas.add(text);
        canvas.setActiveObject(text);
        text.enterEditing();
        setActiveTool('select');
        saveHistory();
      }
    };

    const handleMouseMove = (e: any) => {
      if (!isDrawing || !drawStart || !e.pointer) return;
      if (activeTool === 'select' || activeTool === 'draw' || activeTool === 'text') return;

      if (tempObject) {
        canvas.remove(tempObject);
      }

      const x = Math.min(drawStart.x, e.pointer.x);
      const y = Math.min(drawStart.y, e.pointer.y);
      const width = Math.abs(e.pointer.x - drawStart.x);
      const height = Math.abs(e.pointer.y - drawStart.y);

      if (activeTool === 'rectangle') {
        tempObject = new Rect({
          left: x,
          top: y,
          width,
          height,
          fill: 'transparent',
          stroke: activeColor,
          strokeWidth: 3,
          selectable: false,
        });
      } else if (activeTool === 'circle') {
        const radius = Math.sqrt(width * width + height * height) / 2;
        tempObject = new Circle({
          left: drawStart.x - radius,
          top: drawStart.y - radius,
          radius,
          fill: 'transparent',
          stroke: activeColor,
          strokeWidth: 3,
          selectable: false,
        });
      } else if (activeTool === 'arrow') {
        // Draw line with arrowhead
        const angle = Math.atan2(e.pointer.y - drawStart.y, e.pointer.x - drawStart.x);
        const headLength = 15;

        const arrowHead1 = {
          x: e.pointer.x - headLength * Math.cos(angle - Math.PI / 6),
          y: e.pointer.y - headLength * Math.sin(angle - Math.PI / 6),
        };
        const arrowHead2 = {
          x: e.pointer.x - headLength * Math.cos(angle + Math.PI / 6),
          y: e.pointer.y - headLength * Math.sin(angle + Math.PI / 6),
        };

        tempObject = new Line([drawStart.x, drawStart.y, e.pointer.x, e.pointer.y], {
          stroke: activeColor,
          strokeWidth: 3,
          selectable: false,
        });

        // We'll add arrowhead lines on mouse up
      }

      if (tempObject) {
        canvas.add(tempObject);
        canvas.renderAll();
      }
    };

    const handleMouseUp = (e: any) => {
      if (!isDrawing || !drawStart) return;
      setIsDrawing(false);
      setDrawStart(null);

      if (tempObject) {
        tempObject.selectable = true;
        tempObject.evented = true;

        // Add arrowhead for arrow tool
        if (activeTool === 'arrow' && e.pointer) {
          const angle = Math.atan2(e.pointer.y - drawStart.y, e.pointer.x - drawStart.x);
          const headLength = 15;

          const arrowHead1 = new Line([
            e.pointer.x,
            e.pointer.y,
            e.pointer.x - headLength * Math.cos(angle - Math.PI / 6),
            e.pointer.y - headLength * Math.sin(angle - Math.PI / 6),
          ], {
            stroke: activeColor,
            strokeWidth: 3,
            selectable: true,
          });

          const arrowHead2 = new Line([
            e.pointer.x,
            e.pointer.y,
            e.pointer.x - headLength * Math.cos(angle + Math.PI / 6),
            e.pointer.y - headLength * Math.sin(angle + Math.PI / 6),
          ], {
            stroke: activeColor,
            strokeWidth: 3,
            selectable: true,
          });

          canvas.add(arrowHead1);
          canvas.add(arrowHead2);
        }

        saveHistory();
      }
      tempObject = null;
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [activeTool, activeColor, isDrawing, drawStart, saveHistory]);

  // Handle saving
  const handleSave = useCallback(async () => {
    if (!fabricRef.current) return;
    setIsSaving(true);

    try {
      const dataUrl = fabricRef.current.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 2, // Higher resolution
      });

      const response = await fetch(dataUrl);
      const blob = await response.blob();
      await onSave(blob);
    } catch (error) {
      console.error('Failed to save annotated image:', error);
      alert('Failed to save annotated image. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  const tools: { id: Tool; icon: typeof MousePointer; label: string }[] = [
    { id: 'select', icon: MousePointer, label: 'Select' },
    { id: 'arrow', icon: MoveUpRight, label: 'Arrow' },
    { id: 'rectangle', icon: Square, label: 'Rectangle' },
    { id: 'circle', icon: CircleIcon, label: 'Circle' },
    { id: 'draw', icon: Pencil, label: 'Draw' },
    { id: 'text', icon: Type, label: 'Text' },
  ];

  return (
    <AnimatePresence>
      {/* Force dark mode styling inside the annotator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex flex-col dark"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Annotate Image</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-4 p-4 border-b border-white/10 bg-white/5">
          {/* Tools */}
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
            {tools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                title={tool.label}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  activeTool === tool.id
                    ? 'bg-accent text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                )}
              >
                <tool.icon className="w-5 h-5" />
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-white/20" />

          {/* Colors */}
          <div className="flex items-center gap-1">
            {COLORS.map((color) => (
              <button
                key={color}
                onClick={() => setActiveColor(color)}
                className={cn(
                  'w-7 h-7 rounded-full border-2 transition-all',
                  activeColor === color ? 'border-white scale-110' : 'border-transparent'
                )}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-white/20" />

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={undo}
              disabled={historyIndex <= 0}
              title="Undo"
              className={cn(
                'p-2 rounded-lg transition-colors',
                historyIndex <= 0
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              )}
            >
              <Undo className="w-5 h-5" />
            </button>
            <button
              onClick={clearAll}
              title="Clear All"
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/10 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Save/Cancel */}
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent-light transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Canvas Container */}
        <div
          ref={containerRef}
          className="flex-1 flex items-start justify-center overflow-auto pt-4 px-4 pb-4"
        >
          <canvas ref={canvasRef} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
